import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IDLE, LOADING } from "@constants";
import { getAccountsByUnit } from "@store/core/accounts";
import { getActiveBuildingsByUserAccess } from "@store/core/buildings";
import { getFromServerTopic } from "@utility/slice";
import {
  getAllCurtailmentProgramAdmins,
  getAllCurtailmentProgramPrices,
  getAllCurtailmentPrograms,
} from "../programs/programSlice";

const SLICE = "enrollments";

const action = (name) => `${SLICE}/${name}`;

export const getFromServer = (action, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, payloadFn });

export const getAllEnrollmentRecordsBetween = getFromServerTopic({
  topic: "demandResponse",
  action: "getAllEnrollmentRecordsBetween",
  payloadFn: async (payload) => {
    return payload;
  },
});

export const getAllRevenueSharesBetween = getFromServerTopic({
  topic: "demandResponse",
  action: "getAllRevenueSharesBetween",
  payloadFn: async (payload) => {
    return payload;
  },
});

export const saveRevenueShare = getFromServerTopic({
  topic: "demandResponse",
  action: "saveRevenueShare",
  payloadFn: async (payload) => {
    return payload;
  },
});

export const updateRevenueShare = getFromServerTopic({
  topic: "demandResponse",
  action: "updateRevenueShare",
  payloadFn: async (payload) => {
    return payload;
  },
});

export const saveEnrollmentRecord = getFromServerTopic({
  topic: "demandResponse",
  action: "saveEnrollmentRecord",
  payloadFn: async (payload) => {
    return payload;
  },
});

export const updateEnrollmentRecord = getFromServerTopic({
  topic: "demandResponse",
  action: "updateEnrollmentRecord",
  payloadFn: async (payload) => {
    return payload;
  },
});

const getAllPrograms = createAsyncThunk(
  action("getAllCurtailmentPrograms"),
  async (arg, { dispatch, getState }) => {
    await dispatch(getAllCurtailmentPrograms());
    return getState().curtailmentPrograms.programs;
  },
);

const getAllProgramAdmins = createAsyncThunk(
  action("getAllCurtailmentProgramAdmins"),
  async (arg, { dispatch, getState }) => {
    await dispatch(getAllCurtailmentProgramAdmins());
    return getState().curtailmentPrograms.programAdmins;
  },
);

const getAllProgramPrices = createAsyncThunk(
  action("getAllCurtailmentProgramPrices"),
  async (arg, { dispatch, getState }) => {
    await dispatch(getAllCurtailmentProgramPrices());
    return getState().curtailmentPrograms.programPrices;
  },
);

const getBuildingsForDropdown = createAsyncThunk(
  action("getActiveBuildingsByUserAccess"),
  async (payload, { dispatch, getState }) => {
    await dispatch(getActiveBuildingsByUserAccess());
    return getState().buildings;
  },
);

const getAccountsForDropdown = createAsyncThunk(
  action("getAccounts"),
  async (payload, { dispatch, getState }) => {
    await dispatch(getAccountsByUnit({ unitId: 1 }));
    return getState().accounts;
  },
);

export const initializeEnrollmentsPage = createAsyncThunk(
  action("initializeEnrollmentsPage"),
  async (arg, { dispatch }) =>
    Promise.all([
      dispatch(getAllProgramPrices()),
      dispatch(getAccountsForDropdown()),
      dispatch(getBuildingsForDropdown()),
    ]),
);

export const slice = createSlice({
  name: SLICE,
  initialState: {
    accounts: [],
    buildings: [],
    enrollments: [],
    programs: [],
    programAdmins: [],
    programPrices: [],
    revenueShares: [],
    status: LOADING,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllPrograms.fulfilled, (state, action) => {
        state.programs = action.payload;
      })
      .addCase(getAllProgramAdmins.fulfilled, (state, action) => {
        state.programAdmins = action.payload;
      })
      .addCase(getAllProgramPrices.fulfilled, (state, action) => {
        state.programPrices = action.payload;
      })
      .addCase(getAllRevenueSharesBetween.fulfilled, (state, action) => {
        state.revenueShares = action.payload.response;
      })
      .addCase(saveEnrollmentRecord.fulfilled, (state, action) => {
        console.log("saveEnrollmentRecord.fulfilled", { ...state, ...action });
      })
      .addCase(updateEnrollmentRecord.fulfilled, (state, action) => {
        console.log("updateEnrollmentRecord.fulfilled", {
          ...state,
          ...action,
        });
      })
      .addCase(getAllEnrollmentRecordsBetween.fulfilled, (state, action) => {
        state.enrollments = action.payload.response;
      })
      .addCase(getBuildingsForDropdown.fulfilled, (state, action) => {
        state.buildings = action.payload.buildings
          .filter((building) => building.demandResponseEnabled === 1)
          .map((building) => {
            const { address1, customerOrgId, locationId } = building;
            return {
              address1,
              customerOrgId,
              siteId: locationId,
            };
          });
      })
      .addCase(getAccountsForDropdown.fulfilled, (state, action) => {
        state.accounts = action.payload.accounts.map((account) => {
          const { accountNo, id, name, siteId } = account;
          return {
            accountId: id,
            accountNo,
            name,
            siteId,
          };
        });
      })
      .addCase(initializeEnrollmentsPage.fulfilled, (state) => {
        state.status = IDLE;
      });
  },
});

export default slice.reducer;
